import React, { useState, FC, ReactElement, useEffect } from 'react';
import styled from 'styled-components';
import { Space, Button, Drawer, Collapse } from 'antd';
import AccountSwitcher from '@components/layout/AccountSwitcher';
import Breadcrumbs from '@components/Navbar/Breadcrumbs';
import MemberInfoMenu from '@components/Navbar/MemberInfoMenu';
import { ChangeLanguage } from './components/ChangeLanguage';
import { useUIContext } from '@components/context/ApplicationContext';
import { Timezone } from '@/shared/tz';
import { ContractorStatusModal } from '@components/ContractorStatusView';
import HeaderLogo from '@components/layout/HeaderLogo';
import { Sidebar } from '@components/layout/Sidebar';
import { gettext } from '@cranium/i18n';
import { LocalStorage } from '@/core/Storage';
import { BackgroundOrdersJobService } from '@/core/gql';
import { MobileProfile } from './MobileProfile';
import { CLOSE_DRAWER_MENU } from '@components/Navbar/utils';

const NavContainer = styled.div`
  position: fixed;
  top: 0;
  right: 0;
  width: calc(100% - var(--sidebar-width, 88px));
  height: var(--navbar-height);
  padding: 0 24px;
  display: grid;
  grid-template-columns: auto auto;
  justify-content: space-between;
  align-content: center;
  z-index: 100;
  background-color: #fff;
  @media screen and (max-width: 768px) {
    margin-left: 0;
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: flex-start;
    gap: 12px;
    padding: 0 16px;
    > div * {
      font: var(--font-semibold-20);
    }
    .icon-info-circle1 {
      font-size: 16px;
    }
  }
`;

const Burger = styled(Button)`
  display: none !important;

  @media screen and (max-width: 768px) {
    display: flex !important;
    align-items: center;
    justify-content: center;
    border: 0 !important;
    margin-left: auto !important;
    border-radius: 4px;
    height: 32px !important;
    width: 32px !important;
    background: var(--brand-surface-lihgt) !important;
    padding: 4px !important;
    &:hover {
      background: var(--gray-2) !important;
    }
    i {
      padding: 0 !important;
      vertical-align: 0 !important;
      font-size: 24px;
      color: var(--gray-9);
    }
  }
`;

const MobileSpace = styled(Space)`
  @media screen and (max-width: 768px) {
    display: none !important;
  }
`;

const StyledSidebar = styled(Sidebar)`
  padding: 0;
  overflow: visible;
  min-height: 0;
  flex: 1;
  overflow: auto;
  margin: 0 !important;
`;

const SidebarWrapper = styled.div`
  min-height: 0;
  height: 100%;
  display: flex;
  flex-direction: column;
  gap: 24px;
  margin: 0 !important;
  .ant-select-selector {
    padding: 0 !important;
  }
  .account-switcher {
    display: flex;
    img {
      width: 40px;
    }
    > span {
      font: var(--font-regular-16);
    }
  }
`;

const Label = styled.div`
  font: var(--font-regular-16);
  color: var(--gray-7);
  margin-bottom: 8px;
`;

const StyledAccountSwitcher = styled(AccountSwitcher)`
  display: flex;
  img {
    height: 36px !important;
    width: auto !important;
  }
  img + span {
    display: none !important;
  }
  .ant-select-arrow {
    display: none !important;
  }
`;

interface Props {}

const Section: FC<{ label?: ReactElement }> = ({ children, label }) => {
  return (
    <div>
      <Label>{label}</Label>
      {children}
    </div>
  );
};

export const Navbar: React.FunctionComponent<Props> = () => {
  const {
    sidebar: { minimized, setMinimized }
  } = useUIContext();
  const [open, setOpen] = useState(false);
  useEffect(() => {
    function handler() {
      setOpen(false);
    }
    document.body.addEventListener(CLOSE_DRAWER_MENU, handler);
    return () => {
      document.body.removeEventListener(CLOSE_DRAWER_MENU, handler);
    };
  }, []);
  return (
    <>
      <NavContainer data-main-layout-navbar>
        <MobileLogo />
        <ContractorStatusModal />
        <Breadcrumbs />
        <MobileSpace size={24}>
          <Timezone />
          <ChangeLanguage />
          <AccountSwitcher />
          <MemberInfoMenu />
        </MobileSpace>
        <Burger type="ghost" onClick={() => setOpen(true)}>
          <i className="icon-Menu" />
        </Burger>
      </NavContainer>
      <StyledDrawer
        placement="left"
        onClose={() => {
          setOpen(false);
          setMinimized(false);
        }}
        open={open}
        closable={false}
        destroyOnClose
      >
        <SidebarWrapper>
          <StyledAccountSwitcher isMobile />
          <StyledSidebar mobile minimized={false} />
          <Footer>
            <Collapse
              ghost
              accordion
              destroyInactivePanel
              expandIconPosition="end"
              expandIcon={props => {
                return <Icon className="icon-chevron-right" isActive={props.isActive}></Icon>;
              }}
            >
              <ChangeLanguage mobile />
              <Timezone mobile />
              <MobileProfile />

              <LogOutBtn
                type="default"
                onClick={() => {
                  LocalStorage.clearAll();
                  BackgroundOrdersJobService.stopWatch();
                }}
              >
                <i className="icon-logout1" />
                {gettext('Logout')}
              </LogOutBtn>
            </Collapse>
          </Footer>
        </SidebarWrapper>
      </StyledDrawer>
    </>
  );
};

const StyledDrawer = styled(Drawer)`
  .ant-drawer-body {
    padding: 32px 16px !important;
  }
`;

const MobileLogo = styled(HeaderLogo)`
  display: none !important;
  @media screen and (max-width: 768px) {
    display: flex !important;
  }
  cursor: pointer;
  padding: 0 !important;
  align-items: center !important;
  justify-content: center !important;
  img {
    height: 24px !important;
    min-height: 24px !important;
    width: auto !important;
    max-width: 50px !important;
  }
`;

const Icon = styled.i<{ isActive?: boolean }>`
  font-size: 18px !important;
  color: var(--gray-7) !important;
  transform: rotate(${props => (props.isActive ? -90 : 90)}deg) translate(${props => (props.isActive ? '50%, 0' : '-50%, 0')}) !important;
`;

const Footer = styled.footer`
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  align-items: stretch;
  .ant-collapse {
    border: 0 !important;
    background-color: transparent !important;
  }
  .ant-collapse-header,
  .ant-collapse-header-text {
    padding: 0 !important;
    background-color: transparent !important;
  }

  .ant-collapse-content {
    border: 0 !important;
  }
  .ant-collapse-content-box {
    padding: 8px 0 !important;
  }
`;

const LogOutBtn = styled(Button)`
  display: flex !important;
  height: 48px !important;
  width: 100% !important;
  align-items: center;
  padding: 0 !important;
  gap: 10px !important;
  border: 0 !important;
  border-radius: 4px !important;
  font: var(--font-normal-18) !important;
  color: var(--gray-7) !important;
  padding-left: 8px !important;
  &:hover {
    background: var(--green-2) !important;
    color: var(--gray-9) !important;
  }
  i {
    font-size: 24px !important;
    padding: 0 !important;
  }
`;
